import loader from '@play-co/timestep-core/lib/ui/resource/loader';
import uiConfig, { FontName } from './ui/config';
import type { FontVariant } from './ui/config/fonts';

import { FontMetadataSchema } from '@play-co/timestep-core/lib/ui/bitmapFont/fontRenderer/types';
import { TTFBitmapFont, TTFBitmapFontOpts } from '@play-co/timestep-core/ui';
import { captureGenericError } from './sentry';

const allFontConfigs = uiConfig.fonts;

const fontCache: {
  [fontName: string]: TTFBitmapFont; // & { fontID: FontName };
} = {};

const resourcesFolder = 'assets';
const fontDirectory = `${resourcesFolder}/fonts/`;
const systemFontDirectory = `${fontDirectory}system/`;

function getLanguageDirectory(locale: keyof typeof allFontConfigs) {
  return `${fontDirectory}languages/${locale}/`;
}

function getFontFilePath(
  locale: keyof typeof allFontConfigs,
  fontName: string,
) {
  return `${getLanguageDirectory(locale)}${fontName}.ttf`;
}

function getFontDataPath(
  locale: keyof typeof allFontConfigs,
  fontVariant: FontVariant,
) {
  const directory =
    fontVariant.system === true
      ? systemFontDirectory
      : getLanguageDirectory(locale);

  return `${directory}${fontVariant.fontData}.json`;
}

/** The font metadata files `initializeBitmapFonts` reads for a locale. */
export function getBitmapFontDataPaths(
  locale: keyof typeof allFontConfigs,
): string[] {
  return Object.values(allFontConfigs[locale].variants).map((fontVariant) =>
    getFontDataPath(locale, fontVariant!),
  );
}

/**
 * A font file `initializeBitmapFonts` hands to the renderer, with the metadata
 * of one variant that uses it. Every variant of a file shares its family, so
 * any one variant's metadata names the face the renderer creates for the file.
 */
export interface BitmapFontFile {
  fontPath: string;
  dataPath: string;
}

/** The distinct font files `initializeBitmapFonts` hands to the renderer for a locale. */
export function getBitmapFontFiles(
  locale: keyof typeof allFontConfigs,
): BitmapFontFile[] {
  const files = new Map<string, BitmapFontFile>();

  for (const fontVariant of Object.values(allFontConfigs[locale].variants)) {
    if (fontVariant!.system === true) continue;

    const fontPath = getFontFilePath(locale, fontVariant!.fontName!);
    if (!files.has(fontPath)) {
      files.set(fontPath, {
        fontPath,
        dataPath: getFontDataPath(locale, fontVariant!),
      });
    }
  }

  return [...files.values()];
}

// Loads and embeds the fontID, so it can be reused.
function createTTFFont(
  fontID: FontName,
  opts: TTFBitmapFontOpts,
): TTFBitmapFont & { fontID: FontName } {
  const font = new TTFBitmapFont(opts);

  (font as any).fontID = fontID;
  return font as TTFBitmapFont & { fontID: FontName };
}

export async function initializeBitmapFonts(
  locale: keyof typeof allFontConfigs,
) {
  const fontConfigs = allFontConfigs[locale];
  const fontVariantsMap = fontConfigs.variants;

  const bitmapFontData: any = await loader.loadJSONs(
    getBitmapFontDataPaths(locale),
  );
  const fontIDs = Object.keys(fontVariantsMap);
  for (let f = 0; f < fontIDs.length; f++) {
    const fontID = fontIDs[f];
    const fontConfig = fontVariantsMap[fontID];

    if (!fontConfig) {
      captureGenericError(
        'Cannot load font',
        new Error(`Font config for ${fontID} is missing in ${locale}`),
      );
      continue;
    }

    if (fontConfig.system === true) {
      const font = createTTFFont(fontID as FontName, {
        metadata: bitmapFontData[f] as FontMetadataSchema,
        ...fontConfig.opts,
      });

      fontCache[fontID] = font;
      continue;
    }

    // We can have a single TTF file for several font variants with the same
    // character set. But we need to make sure that `fontFamily` property of each
    // font variant is set to the same value, otherwise the font will fail to
    // load.
    fontCache[fontID] = createTTFFont(fontID as FontName, {
      fontURL: getFontFilePath(locale, fontConfig.fontName!),
      metadata: bitmapFontData[f] as FontMetadataSchema,
      ...fontConfig.opts,
    });
  }

  const fontAliasMap = fontConfigs.aliases;
  for (let alias in fontAliasMap) {
    const fontID = fontAliasMap[alias]!;
    fontCache[alias] = fontCache[fontID];
  }
}

// Legacy function that looked up from by name. Right now is identity
export default function bitmapFonts(name: FontName) {
  return name;
}

export function bitmapFontFromFontID(name: FontName | undefined) {
  if (!name) {
    return null;
  }
  return fontCache[name];
}
